import BScroll, { MouseWheel, ObserveDom, ScrollBar } from "better-scroll";

BScroll.use(MouseWheel);
BScroll.use(ObserveDom);
BScroll.use(ScrollBar);

const defaultOptions = {
  scrollX: false,
  scrollY: true,
  click: true,
  probeType: 3,
  mouseWheel: true,
  observeDOM: true,
  preventDefaultException: {
    tagName: /^(INPUT|TEXTAREA|BUTTON|SELECT|LABEL)$/,
    className:
      /(^|\s)(newCard|fnb-item-card-wrap|c-pointer|click-anim|category-btn-chip|selected_cat)(\s|$)/,
  },
};

const buildOptions = (bindingValue = {}) => {
  const options = bindingValue.options || {};
  return { ...defaultOptions, ...options };
};

const emitScroll = (el, bindingValue = {}) => {
  const callback = bindingValue.onScroll;
  if (!el._betterScroll || typeof callback !== "function") return;
  const bs = el._betterScroll;
  callback({
    x: bs.x,
    y: bs.y,
    maxScrollX: bs.maxScrollX,
    maxScrollY: bs.maxScrollY,
    instance: bs,
  });
};

const bindScrollListener = (el, bindingValue = {}) => {
  if (!el._betterScroll) return;
  if (el._betterScrollHandler) {
    el._betterScroll.off("scroll", el._betterScrollHandler);
    el._betterScrollHandler = null;
  }
  if (typeof bindingValue.onScroll !== "function") return;
  el._betterScrollHandler = () => emitScroll(el, bindingValue);
  el._betterScroll.on("scroll", el._betterScrollHandler);
};

const initBetterScroll = (el, bindingValue = {}) => {
  if (el._betterScroll) return;
  el._betterScroll = new BScroll(el, buildOptions(bindingValue));
  bindScrollListener(el, bindingValue);
};

const refreshBetterScroll = (el) => {
  if (!el._betterScroll) return;
  try {
    el._betterScroll.refresh();
  } catch (e) {
    console.warn("BetterScroll refresh failed", e);
  }
};

const scheduleRefreshBetterScroll = (el) => {
  if (el._bsRefreshTimer) {
    clearTimeout(el._bsRefreshTimer);
  }
  el._bsRefreshTimer = setTimeout(() => {
    el._bsRefreshTimer = null;
    if (!el.isConnected || !el._betterScroll) return;
    refreshBetterScroll(el);
  }, 120);
};

const destroyBetterScroll = (el) => {
  if (el._bsRefreshTimer) {
    clearTimeout(el._bsRefreshTimer);
    el._bsRefreshTimer = null;
  }
  if (!el._betterScroll) return;
  if (el._betterScrollHandler) {
    el._betterScroll.off("scroll", el._betterScrollHandler);
    el._betterScrollHandler = null;
  }
  try {
    el._betterScroll.destroy();
  } catch (e) {
    console.warn("BetterScroll destroy failed", e);
  }
  el._betterScroll = null;
};

const BetterScrollDirective = {
  inserted(el, binding) {
    initBetterScroll(el, binding.value || {});
  },
  componentUpdated(el, binding) {
    const nextValue = binding.value || {};
    if (!el._betterScroll) {
      initBetterScroll(el, nextValue);
      return;
    }
    if (el._bsOnScroll !== nextValue.onScroll) {
      el._bsOnScroll = nextValue.onScroll;
      bindScrollListener(el, nextValue);
    }
    scheduleRefreshBetterScroll(el);
  },
  unbind(el) {
    destroyBetterScroll(el);
  },
};

const DRAG_THRESHOLD = 4;

const resolveAxis = (bindingValue) => {
  if (bindingValue && typeof bindingValue === "object" && bindingValue.axis) {
    return bindingValue.axis;
  }
  if (typeof bindingValue === "string") {
    return bindingValue;
  }
  return "both";
};

const DragScrollDirective = {
  inserted(el, binding) {
    const axis = resolveAxis(binding.value);
    el._dragScrollAxis = axis;

    if (!el.style.touchAction) {
      if (axis === "x") {
        el.style.touchAction = "pan-x";
      } else if (axis === "y") {
        el.style.touchAction = "pan-y";
      } else {
        el.style.touchAction = "pan-x pan-y";
      }
    }

    const onMove = (e) => {
      const state = el._dragScrollState;
      if (!state) return;
      const dx = e.clientX - state.startX;
      const dy = e.clientY - state.startY;
      if (
        !state.moved &&
        Math.abs(dx) < DRAG_THRESHOLD &&
        Math.abs(dy) < DRAG_THRESHOLD
      ) {
        return;
      }
      state.moved = true;
      if (el._dragScrollAxis !== "y") {
        el.scrollLeft = state.startScrollLeft - dx;
      }
      if (el._dragScrollAxis !== "x") {
        el.scrollTop = state.startScrollTop - dy;
      }
    };

    const onUp = () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      el._dragScrollState = null;
    };

    const onDown = (e) => {
      if (e.button !== undefined && e.button !== 0) return;
      el._dragScrollState = {
        startX: e.clientX,
        startY: e.clientY,
        startScrollLeft: el.scrollLeft,
        startScrollTop: el.scrollTop,
        moved: false,
      };
      window.addEventListener("mousemove", onMove);
      window.addEventListener("mouseup", onUp);
    };

    el._dragScrollDown = onDown;
    el._dragScrollMove = onMove;
    el._dragScrollUp = onUp;
    el.addEventListener("mousedown", onDown);
  },
  update(el, binding) {
    el._dragScrollAxis = resolveAxis(binding.value);
  },
  unbind(el) {
    if (el._dragScrollDown) {
      el.removeEventListener("mousedown", el._dragScrollDown);
    }
    if (el._dragScrollMove) {
      window.removeEventListener("mousemove", el._dragScrollMove);
    }
    if (el._dragScrollUp) {
      window.removeEventListener("mouseup", el._dragScrollUp);
    }
    el._dragScrollState = null;
  },
};

export const BetterScrollPlugin = {
  install(Vue) {
    Vue.directive("bscroll", BetterScrollDirective);
    Vue.directive("dragscroll", DragScrollDirective);
  },
};



// WEBPACK FOOTER //
// ./src/plugins/betterScroll.js